import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_URL from '../api';
import { useToast } from '../contexts/ToastContext';
import { ShoppingBag, Coins, Search, Clock, CheckCircle2, XCircle, Gift } from 'lucide-react';
import { resolveAssetUrl } from '../utils/assetUrl';
import './Store.css';

const Store = ({ student, onBalanceUpdate }) => {
    const [items, setItems] = useState([]);
    const [purchases, setPurchases] = useState([]);
    const [activeSubTab, setActiveSubTab] = useState('shop'); // 'shop' or 'history'
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(true);
    const [purchasingItem, setPurchasingItem] = useState(null);
    const [notes, setNotes] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    const { success, error } = useToast();

    useEffect(() => {
        fetchStoreData();
    }, [activeSubTab]);

    const fetchStoreData = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            if (activeSubTab === 'shop') {
                const res = await axios.get(`${API_URL}/store/items`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setItems(res.data);
            } else {
                const res = await axios.get(`${API_URL}/store/purchases/my`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setPurchases(res.data);
            }
        } catch (err) {
            error(err.response?.data?.error || 'Помилка завантаження даних магазину');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenPurchaseModal = (item) => {
        setPurchasingItem(item);
        setNotes('');
    };

    const handleClosePurchaseModal = () => {
        if (!isSubmitting) {
            setPurchasingItem(null);
        }
    };

    const handleConfirmPurchase = async () => {
        if (!purchasingItem) return;
        setIsSubmitting(true);
        try {
            const token = localStorage.getItem('token');
            const res = await axios.post(`${API_URL}/store/purchase`, {
                itemId: purchasingItem._id,
                notes
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            success('Замовлення оформлено успішно! Зверніться до адміністратора для отримання.');
            if (onBalanceUpdate && typeof onBalanceUpdate === 'function') {
                onBalanceUpdate(res.data.newBalance);
            }
            // Update student coins locally in current view
            student.coins = res.data.newBalance;
            
            setPurchasingItem(null);
            fetchStoreData();
        } catch (err) {
            error(err.response?.data?.error || 'Не вдалося оформити покупку');
        } finally {
            setIsSubmitting(false);
        }
    };

    const filteredItems = items.filter(item => 
        item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (item.description && item.description.toLowerCase().includes(searchQuery.toLowerCase()))
    );

    const getStatusBadge = (status) => {
        switch (status) {
            case 'pending':
                return <span className="store-badge badge-pending"><Clock size={12} /> Очікує видачі</span>;
            case 'completed':
                return <span className="store-badge badge-completed"><CheckCircle2 size={12} /> Отримано</span>;
            case 'canceled':
                return <span className="store-badge badge-canceled"><XCircle size={12} /> Скасовано / Повернено</span>;
            default:
                return <span className="store-badge">{status}</span>;
        }
    };

    return (
        <div className="store-container">
            {/* Header section with Balance Card */}
            <div className="store-header-card">
                <div className="store-header-info">
                    <h2>🏪 Магазин подарунків ServIQ</h2>
                    <p>Навчайтеся старанно, накопичуйте ServIQ Coins та обмінюйте їх на фірмовий мерч, призи чи приємні бонуси!</p>
                </div>
                <div className="store-balance-badge">
                    <div className="coin-anim-wrapper">
                        <Coins size={36} className="coin-icon-glow" />
                    </div>
                    <div className="balance-info">
                        <span className="balance-title">Мій баланс:</span>
                        <span className="balance-value">{student.coins || 0} <span>Coins</span></span>
                    </div>
                </div>
            </div>

            {/* Navigation tabs */}
            <div className="store-subtabs-row">
                <div className="store-subtabs">
                    <button 
                        className={`store-subtab-btn ${activeSubTab === 'shop' ? 'active' : ''}`}
                        onClick={() => setActiveSubTab('shop')}
                    >
                        <ShoppingBag size={18} />
                        <span>Вітрина товарів</span>
                    </button>
                    <button 
                        className={`store-subtab-btn ${activeSubTab === 'history' ? 'active' : ''}`}
                        onClick={() => setActiveSubTab('history')}
                    >
                        <Clock size={18} />
                        <span>Історія покупок</span>
                    </button>
                </div>

                {activeSubTab === 'shop' && (
                    <div className="store-search-box">
                        <Search size={18} className="store-search-icon" />
                        <input 
                            type="text" 
                            placeholder="Пошук товарів..." 
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                )}
            </div>

            {/* Main Content Area */}
            {loading ? (
                <div className="store-loading">
                    <div className="store-spinner"></div>
                    <p>Завантаження магазину...</p>
                </div>
            ) : activeSubTab === 'shop' ? (
                filteredItems.length === 0 ? (
                    <div className="store-empty">
                        <ShoppingBag size={48} />
                        <p>{searchQuery ? 'За вашим запитом нічого не знайдено' : 'Наразі в магазині немає доступних товарів'}</p>
                    </div>
                ) : (
                    <div className="store-grid">
                        {filteredItems.map((item) => {
                            const isAffordable = (student.coins || 0) >= item.price;
                            const isOutOfStock = item.stock === 0;
                            
                            return (
                                <div key={item._id} className={`store-card ${isOutOfStock ? 'out-of-stock' : ''}`}>
                                    {item.imageUrl ? (
                                        <div className="store-card-img" style={{ backgroundImage: `url(${resolveAssetUrl(item.imageUrl)})` }} />
                                    ) : (
                                        <div className="store-card-img-placeholder">
                                            <div className="placeholder-icon-circle">
                                                <Gift size={32} />
                                            </div>
                                        </div>
                                    )}
                                    <div className="store-card-content">
                                        <h3>{item.title}</h3>
                                        <p className="store-card-desc">{item.description || 'Опис відсутній'}</p>
                                        
                                        <div className="store-card-meta">
                                            <div className="store-card-price">
                                                <Coins size={16} />
                                                <span>{item.price} Coins</span>
                                            </div>
                                            <div className="store-card-stock">
                                                {item.stock === -1 ? 'Безлімітно' : `Залишилось: ${item.stock} шт.`}
                                            </div>
                                        </div>
                                        
                                        <button 
                                            className="store-btn-buy"
                                            disabled={!isAffordable || isOutOfStock}
                                            onClick={() => handleOpenPurchaseModal(item)}
                                        >
                                            {isOutOfStock ? 'Немає в наявності' : !isAffordable ? 'Недостатньо монет' : 'Купити'}
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )
            ) : (
                purchases.length === 0 ? (
                    <div className="store-empty">
                        <Clock size={48} />
                        <p>Ви ще не купували товари в нашому магазині</p>
                    </div>
                ) : (
                    <div className="store-history-list">
                        {purchases.map((pur) => (
                            <div key={pur._id} className="store-history-item">
                                <div className="store-history-info">
                                    <h4>{pur.itemId?.title || 'Видалений товар'}</h4>
                                    <span className="store-history-date">
                                        Замовлено: {new Date(pur.createdAt).toLocaleDateString('uk-UA')} {new Date(pur.createdAt).toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit' })}
                                    </span>
                                    {pur.notes && <p className="store-history-notes">📝 Коментар: <i>{pur.notes}</i></p>}
                                </div>
                                <div className="store-history-status-group">
                                    <div className="store-history-price">
                                        <Coins size={14} />
                                        <span>{pur.coinsSpent} Coins</span>
                                    </div>
                                    {getStatusBadge(pur.status)}
                                </div>
                            </div>
                        ))}
                    </div>
                )
            )}

            {/* Purchase Confirmation Modal */}
            {purchasingItem && (
                <div className="store-modal-overlay" onClick={handleClosePurchaseModal}>
                    <div className="store-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="store-modal-header">
                            <h3>Підтвердження покупки</h3>
                            <button className="store-modal-close" onClick={handleClosePurchaseModal} disabled={isSubmitting}>&times;</button>
                        </div>
                        <div className="store-modal-body">
                            <div className="store-confirm-item">
                                {purchasingItem.imageUrl && <img src={resolveAssetUrl(purchasingItem.imageUrl)} alt={purchasingItem.title} />}
                                <div>
                                    <h4>{purchasingItem.title}</h4>
                                    <p>{purchasingItem.description}</p>
                                    <div className="store-confirm-cost">
                                        Вартість: <strong>🪙 {purchasingItem.price} Coins</strong>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="store-confirm-balance-math">
                                <div>Поточний баланс: <span>{student.coins || 0} Coins</span></div>
                                <div>Баланс після покупки: <span className="math-result">{(student.coins || 0) - purchasingItem.price} Coins</span></div>
                            </div>

                            <div className="store-confirm-notes">
                                <label htmlFor="notes-field">Коментар до замовлення (необов'язково):</label>
                                <textarea 
                                    id="notes-field"
                                    placeholder="Вкажіть розмір одягу, колір або інші побажання..."
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    disabled={isSubmitting}
                                    maxLength={300}
                                />
                            </div>
                        </div>
                        <div className="store-modal-footer">
                            <button 
                                className="store-modal-btn btn-cancel" 
                                onClick={handleClosePurchaseModal}
                                disabled={isSubmitting}
                            >
                                Скасувати
                            </button>
                            <button 
                                className="store-modal-btn btn-confirm"
                                onClick={handleConfirmPurchase}
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? 'Оформлення...' : 'Підтвердити покупку'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Store;
